import { type InteractionType, type ProspectContactDto, type ProspectInteractionDto } from "@crowdcoursing/api/openapi";
import { Box, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { DateTime } from "luxon";
import { Field, Form } from "react-final-form";

import { INTERACTION_TYPES } from "~utils/constants/interactionTypes";

interface InteractionFormValues extends Omit<ProspectInteractionDto, "id" | "prospectId" | "userId" | "createdAt"> {
  type: InteractionType;
  subject: string;
  notes: string;
  contactId: string | null;
  occurredAt: string;
}

interface InteractionFormProps {
  contacts?: ProspectContactDto[];
  initialValues?: Partial<InteractionFormValues>;
  onSubmit: (values: InteractionFormValues) => void;
}

export const InteractionForm = ({ contacts, initialValues, onSubmit }: InteractionFormProps) => {
  return (
    <Form<InteractionFormValues>
      initialValues={{
        contactId: null,
        notes: "",
        occurredAt: DateTime.now().toISO(),
        subject: "",
        type: initialValues?.type ?? 1,
        ...initialValues,
      }}
      onSubmit={onSubmit}
      render={({ handleSubmit }) => (
        <form id="interaction-form" onSubmit={handleSubmit}>
          <Box display="grid" gap={3}>
            {/* Type */}
            <Field name="type">
              {({ input }) => (
                <FormControl fullWidth>
                  <InputLabel id="type-label">Type</InputLabel>
                  <Select {...input} labelId="type-label" label="Type">
                    {INTERACTION_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box component="span" sx={{ color: type.color }}>
                            {type.icon}
                          </Box>
                          {type.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Field>

            {/* Contact */}
            {contacts && contacts.length > 0 && (
              <Field name="contactId">
                {({ input }) => (
                  <FormControl fullWidth>
                    <InputLabel id="contact-label">Contact</InputLabel>
                    <Select {...input} labelId="contact-label" label="Contact">
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {contacts.map((contact) => (
                        <MenuItem key={contact.id} value={contact.id}>
                          {[contact.firstName, contact.lastName].filter(Boolean).join(" ")}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Field>
            )}

            {/* Subject */}
            <Field name="subject">
              {({ input }) => (
                <TextField {...input} label="Subject" required fullWidth placeholder="What was discussed?" />
              )}
            </Field>

            {/* Notes */}
            <Field name="notes">
              {({ input }) => (
                <TextField
                  {...input}
                  label="Notes"
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Add any additional details..."
                />
              )}
            </Field>

            {/* Time (Date fixed to today) */}
            <Field name="occurredAt">
              {({ input }) => {
                // Always use today's date
                const today = DateTime.now().startOf("day");
                const currentValue = input.value ? DateTime.fromISO(input.value) : DateTime.now();
                // Extract just the time from the current value
                const timeOnly = currentValue.toFormat("HH:mm");
                const formattedDate = today.toFormat("MMM d, yyyy");

                return (
                  <Box>
                    <Box mb={1} display="flex" alignItems="center">
                      <Box component="span" fontWeight="medium" mr={1}>
                        Date:
                      </Box>
                      <Box component="span">{formattedDate} (Today)</Box>
                    </Box>
                    <TextField
                      label="Time"
                      type="time"
                      value={timeOnly}
                      onChange={(e) => {
                        // Parse the time input and combine with today's date
                        const [hours, minutes] = e.target.value.split(":").map(Number);
                        const newDateTime = today.set({ hour: hours, minute: minutes }).toISO();
                        input.onChange(newDateTime);
                      }}
                      fullWidth
                      required
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        step: 60, // 1 minute steps
                      }}
                    />
                  </Box>
                );
              }}
            </Field>
          </Box>
        </form>
      )}
    />
  );
};
